import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck, Truck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lines = [
  { text: "MOTORI USATI", cls: "text-[#F3F4F6]" },
  { text: "TESTATI.", cls: "text-[#F59E0B] italic" },
  { text: "PRONTI A RIPARTIRE.", cls: "text-stroke-amber" },
];

const lineVariant = {
  hidden: { y: "115%", rotate: 2 },
  show: (i) => ({
    y: 0,
    rotate: 0,
    transition: { delay: 0.25 + i * 0.14, duration: 0.95, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const [total, setTotal] = useState(null);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 260]);
  const fade = useTransform(scrollY, [0, 600], [1, 0.25]);

  useEffect(() => {
    axios
      .get(`${API}/engines/meta`)
      .then((r) => setTotal(r.data.total))
      .catch(() => {});
  }, []);

  const goCatalog = () => {
    const el = document.getElementById("catalogo");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -64 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" data-testid="hero-section" className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden border-b border-[#272A38]">
      <motion.div style={{ y: bgY, opacity: fade }} className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1552656967-7a0991a13906?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="Motore in officina"
          className="h-[120%] w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10]/60 via-[#0B0C10]/55 to-[#0B0C10]" />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(circle at 70% 30%, rgba(245,158,11,0.14) 0%, rgba(11,12,16,0) 60%)" }}
        />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-36 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.7 }}
          className="mono mb-6 text-xs uppercase tracking-[0.3em] text-[#F59E0B]"
          data-testid="hero-eyebrow"
        >
          Autodemolizione — Delta R.E.M. S.r.l. · Cremona
        </motion.p>

        <h1 className="font-display text-6xl font-black uppercase leading-[0.9] tracking-tight sm:text-8xl lg:text-[7.5rem]" data-testid="hero-headline">
          {lines.map((l, i) => (
            <span key={l.text} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
                className={`block origin-left ${l.cls}`}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between"
        >
          <div className="max-w-md">
            <p className="text-base leading-relaxed text-[#9CA3AF]" data-testid="hero-subtitle">
              Oltre {total ? total.toLocaleString("it-IT") : "1.800"} motori usati in catalogo, ognuno
              testato e verificato dai nostri meccanici. Trova il tuo codice motore e
              richiedilo in un messaggio su WhatsApp.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-catalog-cta"
                onClick={goCatalog}
                className="group flex items-center gap-3 bg-[#F59E0B] px-7 py-4 font-display text-lg font-bold uppercase tracking-wide text-[#0B0C10] transition-colors duration-300 hover:bg-[#D97706]"
              >
                Esplora il catalogo
                <ArrowDown className="h-5 w-5 transition-transform duration-300 group-hover:translate-y-1" />
              </button>
              <div className="mono flex items-center gap-5 text-[11px] uppercase tracking-widest text-[#6B7280]">
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[#F59E0B]" /> Testati e verificati
                </span>
                <span className="flex items-center gap-2">
                  <Truck className="h-4 w-4 text-[#F59E0B]" /> Spedizione in tutta Italia
                </span>
              </div>
            </div>
          </div>

          <div className="mono flex gap-8 text-right sm:flex-col sm:gap-3" data-testid="hero-stats">
            <div>
              <div className="font-display text-4xl font-black text-[#F3F4F6]">
                {total ? total.toLocaleString("it-IT") : "—"}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">Motori in catalogo</div>
            </div>
            <div>
              <div className="font-display text-4xl font-black text-[#F3F4F6]">15+</div>
              <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">Marchi trattati</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
